import React, {useState} from "react"
import Avatar from "react-avatar"
import {default as cx} from "classnames"
import {Skeleton} from "primereact/skeleton"

import {OrientationReadinessColumn} from "../orientation/OrientationReadinessColumn"
import {OrientationReadinessChart} from "../orientation/OrientationReadinessChart"
import {OrientationWeekDetails} from "../orientation/OrientationWeekDetails"
import {scoreBadge, getLocaleDate} from "../../utilities/general"
import {wrappedGet} from "../../utilities/AxiosHelper"

export const EducatorOrientationArchiveSummary = ({orientation, preceptors}) => {
    const [showDetails, setShowDetails] = useState(false)
    const [details, setDetails] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [visibleWeeks, setVisibleWeeks] = useState(3)
    const osrMax = scoreBadge(orientation.current_readiness_rating, 100)

    const fetchOrientationDetails = async (id) => {
        if (!showDetails) {
            setIsLoading(true)
            const searchOrientation = await wrappedGet(`/orientation-details?orientation-id=${id}`)
            setDetails(searchOrientation.data.results)
            setShowDetails(true)
            setIsLoading(false)
        } else {
            setShowDetails(false)
        }
    }

    return (
        <div className="card mt-4">
            <div className="card-header flex-column flex-sm-row align-items-stretch align-items-sm-center gap-4 p-4 p-sm-6">
                <div className="card-title text-dark fw-bold gap-4 flex-fill">
                    <div className="d-flex flex-row flex-fill">
                        <div className="d-flex flex-column fs-5">
                            <div className="d-flex flex-row">
                                <Avatar
                                    name={orientation.name}
                                    size="40"
                                    round="0.475rem"
                                    maxInitials={2}
                                    color="var(--bs-primary)"
                                    fgColor="var(--bs-primary-inverse, #fff)"
                                />
                                <div className="d-flex flex-column fs-5 px-3">
                                    <div className="d-flex flex-row">{orientation.name}</div>
                                    <div className="d-flex flex-row flex-wrap align-items-center gap-3">
                                        <span className="text-dark fw-bold d-block fs-7 ">
                                            Status: {orientation.orientation_status}
                                        </span>
                                        <span className="fw-semibold d-block fs-7">
                                            <i
                                                className={cx("fas fa-calendar-check me-1 fs-5", {
                                                    "text-success": orientation.orientation_status === "Completed",
                                                    "text-danger": orientation.orientation_status === "Attritioned",
                                                    "text-warning": orientation.orientation_status === "Cancelled",
                                                })}
                                            ></i>
                                            {orientation.end_date ? getLocaleDate(orientation.end_date) : "No Info"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-toolbar gap-4 flex-grow-1 flex-md-grow-0 justify-content-between w-100 w-sm-auto">
                    <div className="d-flex flex-row flex-fill flex-end justify-content-end align-items-center ms-0 ms-sm-4">
                        {osrMax.score && (
                            <div className={cx("badge px-4 py-3", osrMax.background, osrMax.textColor)}>
                                {osrMax.score}%
                            </div>
                        )}
                    </div>
                    <button
                        className={cx("btn btn-light-primary rotate collapsible", {collapsed: !showDetails})}
                        onClick={() => {
                            fetchOrientationDetails(orientation.id)
                        }}
                    >
                        Details
                        <span className="d-flex flex-center rotate-180 ms-3">
                            <i className="fas fa-arrow-circle-down fs-3"></i>
                        </span>
                    </button>
                </div>
            </div>

            {isLoading && <Skeleton className="w-100 h-80px mb-3" />}

            {showDetails && (
                <div className="card-body py-4">
                    <div className="row row-cols-1 row-cols-lg-2 g-4">
                        <div className="col">
                            <div className="d-flex flex-wrap fw-semibold fs-6 mb-md-0 mb-4 pe-2 gap-2">
                                <span className="d-flex align-items-center me-5 mb-2">
                                    <i className={"fas text-primary fs-6 me-2 fa-user-nurse"}></i>
                                    {details.position_name ? details.position_name : "No Info"}
                                </span>
                                <span className="d-flex align-items-center me-5 mb-2">
                                    <i className={"fas text-primary fs-6 me-2 fa-hospital"}></i>
                                    {details.unit_name ? details.unit_name : "No Info"}
                                </span>
                                <span className="d-flex align-items-center me-5 mb-2">
                                    <i className="fas text-primary fs-6 me-2 fa-calendar-week"></i>
                                    Weeks:
                                    {details.standard_orientation_length_weeks
                                        ? `${details.standard_orientation_length_weeks}`
                                        : "No Info"}
                                </span>
                                <span className="d-flex align-items-center me-5 mb-2">
                                    <i className="fas text-primary fs-6 me-2 fa-calendar"></i>
                                    Start Date:
                                    {details.start_date ? getLocaleDate(details.start_date) : "No Info"}
                                </span>

                                <span className="d-flex align-items-center me-5 mb-2">
                                    <i className="fas text-primary fs-6 me-2 fa-calendar"></i>
                                    End Date:
                                    {details.end_date ? getLocaleDate(details.end_date) : "No Info"}
                                </span>
                            </div>
                        </div>
                        {details.current_percent_max_osr_score !== undefined && (
                            <OrientationReadinessColumn percentOSRScore={details.current_percent_max_osr_score} />
                        )}
                        <div className="card-body px-4">
                            <OrientationReadinessChart orientee={details} />
                            <div className="d-flex flex-column gap-2">
                                {Object.keys(details.weeks)
                                    .sort((a, b) => parseInt(b) - parseInt(a))
                                    .slice(0, visibleWeeks)
                                    .map((week) => {
                                        const weekData = details.weeks[week]
                                        return (
                                            <OrientationWeekDetails
                                                key={week}
                                                week={week}
                                                weekData={weekData}
                                                orientation={orientation}
                                            />
                                        )
                                    })}
                            </div>
                            {visibleWeeks < Object.keys(details.weeks).length && (
                                <div className="d-flex flex-column mt-4">
                                    <button
                                        className="btn btn-light-primary"
                                        onClick={() => setVisibleWeeks(visibleWeeks + 3)}
                                    >
                                        Load More Weeks
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
